import type { IncomeBoat, IncomeCell } from '@sea-snag/shared';

// Analytics → Income (#217). The API sends each boat's cells, already split in
// cents; everything on the page is summed from them here, so the cards, the
// chart and the table always agree, whatever the filters keep.

export type IncomeMetric = 'gross' | 'net' | 'owner' | 'company';
export type OwnershipFilter = 'all' | 'company' | 'external';

export interface IncomeFilters {
  /** Part of a boat's name, any case. */
  search: string;
  /** An owner's pid, or 'all'. */
  ownerPid: string;
  ownership: OwnershipFilter;
}

export const NO_FILTERS: IncomeFilters = { search: '', ownerPid: 'all', ownership: 'all' };

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Whether a boat's owner/company split is known: company-owned, or external with a share. */
export function splitKnown(boat: Pick<IncomeBoat, 'ownership' | 'sharePct'>): boolean {
  return boat.ownership === 'company' || (boat.ownership === 'external' && boat.sharePct !== null);
}

/**
 * The boats the filters keep. A boat whose ownership is unresolved is kept
 * under All, with its setup note, and by neither ownership type.
 */
export function filterBoats(boats: readonly IncomeBoat[], f: IncomeFilters): IncomeBoat[] {
  const search = f.search.trim().toLowerCase();
  return boats.filter(
    (b) =>
      (f.ownership === 'all' || b.ownership === f.ownership) &&
      (f.ownerPid === 'all' || b.owner?.pid === f.ownerPid) &&
      (search === '' || b.name.toLowerCase().includes(search))
  );
}

export interface IncomeTotals {
  bookings: number;
  days: number;
  guestBookings: number;
  guestDays: number;
  grossCents: number;
  taxCents: number;
  netCents: number;
  guestNetCents: number;
  /** The allocations that are known; the rest of the net is `unallocatedNetCents`. */
  ownerCents: number;
  companyCents: number;
  /** Boats with income whose split isn't known: ownership unresolved, or no owner share. */
  unresolvedBoats: number;
  unallocatedNetCents: number;
}

/** Everything the boats earned in one currency, optionally only some of their cells. */
export function totalsOf(
  boats: readonly IncomeBoat[],
  currency: string,
  include: (c: IncomeCell) => boolean = () => true
): IncomeTotals {
  const t: IncomeTotals = {
    bookings: 0,
    days: 0,
    guestBookings: 0,
    guestDays: 0,
    grossCents: 0,
    taxCents: 0,
    netCents: 0,
    guestNetCents: 0,
    ownerCents: 0,
    companyCents: 0,
    unresolvedBoats: 0,
    unallocatedNetCents: 0,
  };
  for (const b of boats) {
    let unresolved = false;
    for (const c of b.cells) {
      if (c.currency !== currency || !include(c)) continue;
      t.bookings += c.bookings;
      t.days += c.days;
      t.guestBookings += c.guestBookings;
      t.guestDays += c.guestDays;
      t.grossCents += c.grossCents;
      t.taxCents += c.taxCents;
      t.netCents += c.netCents;
      t.guestNetCents += c.guestNetCents;
      if (c.ownerCents === null || c.companyCents === null) {
        t.unallocatedNetCents += c.netCents;
        if (c.netCents !== 0) unresolved = true;
      } else {
        t.ownerCents += c.ownerCents;
        t.companyCents += c.companyCents;
      }
    }
    if (unresolved) t.unresolvedBoats += 1;
  }
  return t;
}

export interface MonthPoint {
  year: number;
  month: number;
  /** "Jul", or "Jan 2027" for a month of the next year. */
  label: string;
  /** The metric's known amount. */
  cents: number;
  /** Part of the month's amount is unknown: shown as incomplete, never as a smaller figure. */
  incomplete: boolean;
}

/**
 * One point per month, in the month charters ended: the season's twelve, then
 * any month of the next year a December charter ran into.
 */
export function monthlySeries(
  boats: readonly IncomeBoat[],
  currency: string,
  year: number,
  metric: IncomeMetric
): MonthPoint[] {
  const extra = new Set<number>();
  for (const b of boats) {
    for (const c of b.cells) if (c.currency === currency && c.year > year) extra.add(c.month);
  }
  const months = [
    ...MONTHS.map((_, i) => ({ year, month: i + 1 })),
    ...[...extra].sort((a, b) => a - b).map((month) => ({ year: year + 1, month })),
  ];
  return months.map(({ year: y, month }) => {
    const t = totalsOf(boats, currency, (c) => c.year === y && c.month === month);
    const cents =
      metric === 'gross'
        ? t.grossCents
        : metric === 'net'
          ? t.netCents
          : metric === 'owner'
            ? t.ownerCents
            : t.companyCents;
    return {
      year: y,
      month,
      label: y === year ? MONTHS[month - 1]! : `${MONTHS[month - 1]} ${y}`,
      cents,
      incomplete: (metric === 'owner' || metric === 'company') && t.unallocatedNetCents !== 0,
    };
  });
}

export interface BoatRowTotals {
  bookings: number;
  /** Booked charter weeks: days / 7. */
  weeks: number;
  grossCents: number;
  taxCents: number;
  netCents: number;
  /** Null while the boat's split isn't known, even with nothing booked. */
  ownerCents: number | null;
  companyCents: number | null;
}

/** A boat's row in one currency. */
export function boatRow(boat: IncomeBoat, currency: string): BoatRowTotals {
  const t = totalsOf([boat], currency);
  const known = splitKnown(boat) && t.unallocatedNetCents === 0;
  return {
    bookings: t.bookings,
    weeks: t.days / 7,
    grossCents: t.grossCents,
    taxCents: t.taxCents,
    netCents: t.netCents,
    ownerCents: known ? t.ownerCents : null,
    companyCents: known ? t.companyCents : null,
  };
}

const formats = new Map<string, Intl.NumberFormat>();
/** Cents as money in their own currency: whole units, or to the cent where exactness matters. */
export function formatCents(cents: number, currency: string, decimals: 0 | 2 = 0): string {
  const key = `${currency}|${decimals}`;
  let format = formats.get(key);
  if (!format) {
    format = new Intl.NumberFormat('en', {
      style: 'currency',
      currency,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    formats.set(key, format);
  }
  return format.format(cents / 100);
}
